import { Token } from "./";

// Very small but decent JS tokenizer
export function tokenizeJs(line: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    const push = (type: string, len: number) => {
        if (len > 0) tokens.push({ type, text: line.slice(i, i + len) });
        i += len;
    };
    while (i < line.length) {
        const c = line[i];
        if (/[a-zA-Z_$]/.test(c)) {
            let j = i;
            while (j < line.length && /[a-zA-Z0-9_$]/.test(line[j])) j++;
            const word = line.slice(i, j);
            const isKeyword = ["function", "const", "let", "var", "return", "if", "else", "do", "while", "try", "catch", "finally", "true", "false", "null", "undefined", "class", "import", "export", "from", "async", "await", "type"].includes(word);
            push(isKeyword ? "keyword" : "identifier", j - i);
        } else if (c === '"' || c === "'" || c === "`") {
            const quote = c;
            let j = i + 1;
            while (j < line.length && line[j] !== quote) { if (line[j] === "\\") j++; j++; }
            if (line[j] === quote) j++;
            push("string", j - i);
        } else if (c === "/" && (line[i + 1] === "/" || line[i + 1] === "*")) {
            const endIdx = line.indexOf("*/", i + 2);
            push("comment", line[i + 1] === "/" ? line.length - i : (endIdx === -1 ? line.length : endIdx + 2) - i);
        } else if (/[0-9]/.test(c)) {
            let j = i;
            while (j < line.length && /[0-9a-fA-Fxob.]/.test(line[j])) j++;
            push("number", j - i);
        } else if ("+-*/%=&|!^<>?:".includes(c)) {
            push("operator", ["==", "!=", "<=", ">=", "+=", "-=", "*=", "/=", "=>", "&&", "||"].includes(line.slice(i, i + 2)) ? 2 : 1);
        } else if ("()[]{}".includes(c)) {
            push("syntax", 1);
        } else if (c === ' ') {
            // Count consecutive spaces and merge into single token
            let j = i;
            while (j < line.length && line[j] === ' ') j++;
            const spaceCount = j - i;
            // Use Unicode non-breaking space to preserve spacing
            if (i == 0) {
                tokens.push({ type: "leadingwhitespace", text: '\u00B7'.repeat(spaceCount) }); //&middot;
            } else {
                tokens.push({ type: "", text: '\u00A0'.repeat(spaceCount) });
            }

            i = j;  // Skip past all the spaces
            continue;  // Skip the i++ at end of loop
        } else {
            push("", 1);
        }
    }
    return tokens;
}

// Markdown tokenizer
export function tokenizeMd(line: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;
    const push = (type: string, len: number) => {
        if (len > 0) tokens.push({ type, text: line.slice(i, i + len) });
        i += len;
    };

    // Check for headers at start of line
    if (line.trimStart().startsWith('#')) {
        const leadingSpaces = line.length - line.trimStart().length;
        if (leadingSpaces > 0) {
            tokens.push({ type: "", text: '\u00A0'.repeat(leadingSpaces) });
            i = leadingSpaces;
        }
        let j = i;
        while (j < line.length && line[j] === '#') j++;
        if (j < line.length && line[j] === ' ') {
            push("keyword", j - i + 1); // Include the space after #
            // Rest of line is header text
            while (i < line.length) {
                push("keyword", 1);
            }
            return tokens;
        }
    }

    while (i < line.length) {
        const c = line[i];
        const remaining = line.slice(i);

        // Inline code
        if (c === '`') {
            let j = i + 1;
            while (j < line.length && line[j] !== '`') j++;
            if (j < line.length) j++; // Include closing backtick
            push("string", j - i);
        }
        // Bold **text** or __text__
        else if ((c === '*' && line[i + 1] === '*') || (c === '_' && line[i + 1] === '_')) {
            const marker = c + c;
            let j = i + 2;
            while (j < line.length - 1 && line.slice(j, j + 2) !== marker) j++;
            if (line.slice(j, j + 2) === marker) j += 2;
            push("keyword", j - i);
        }
        // Italic *text* or _text_
        else if ((c === '*' || c === '_') && line[i + 1] !== c) {
            let j = i + 1;
            while (j < line.length && line[j] !== c) j++;
            if (j < line.length) j++;
            push("keyword", j - i);
        }
        // Links [text](url)
        else if (c === '[') {
            let j = i + 1;
            while (j < line.length && line[j] !== ']') j++;
            if (j < line.length && line[j + 1] === '(') {
                j += 2;
                while (j < line.length && line[j] !== ')') j++;
                if (j < line.length) j++;
            } else if (j < line.length) {
                j++;
            }
            push("identifier", j - i);
        }
        // Images ![alt](url)
        else if (c === '!' && line[i + 1] === '[') {
            let j = i + 2;
            while (j < line.length && line[j] !== ']') j++;
            if (j < line.length && line[j + 1] === '(') {
                j += 2;
                while (j < line.length && line[j] !== ')') j++;
                if (j < line.length) j++;
            } else if (j < line.length) {
                j++;
            }
            push("identifier", j - i);
        }
        // List markers (-, *, +) at start or after spaces
        else if ((c === '-' || c === '*' || c === '+') && (i === 0 || line.slice(0, i).trim() === '') && line[i + 1] === ' ') {
            push("operator", 1);
        }
        // Numbered lists
        else if (/[0-9]/.test(c) && (i === 0 || line.slice(0, i).trim() === '')) {
            let j = i;
            while (j < line.length && /[0-9]/.test(line[j])) j++;
            if (j < line.length && line[j] === '.' && line[j + 1] === ' ') {
                push("number", j - i + 2); // Include dot and space
            } else {
                push("", 1);
            }
        }
        // Code fence markers
        else if (remaining.startsWith('```') || remaining.startsWith('~~~')) {
            const marker = remaining.startsWith('```') ? '```' : '~~~';
            let j = i + 3;
            // Include language identifier if present
            while (j < line.length && /[a-zA-Z0-9]/.test(line[j])) j++;
            push("comment", j - i);
        }
        // Blockquote
        else if (c === '>' && (i === 0 || line.slice(0, i).trim() === '')) {
            push("comment", 1);
        }
        // Horizontal rule
        else if ((c === '-' || c === '*' || c === '_') && remaining.match(/^([-*_])\1{2,}\s*$/)) {
            push("operator", line.length - i);
        }
        // Spaces
        else if (c === ' ') {
            let j = i;
            while (j < line.length && line[j] === ' ') j++;
            const spaceCount = j - i;
            tokens.push({ type: "", text: '\u00A0'.repeat(spaceCount) });
            i = j;
            continue;
        }
        // Everything else
        else {
            push("", 1);
        }
    }
    return tokens;
}
